// Ruled-register line chart: ink lines on ruled paper, gold emphasis. Pure SVG.
import React from 'react';

export interface LinePoint { x: number; y: number }
export interface LineSeries {
  label: string;
  points: LinePoint[];
  color: string;
  dashed?: boolean;
  emphasis?: boolean;
}

interface Props {
  series: LineSeries[];
  xTicks: number[];
  height?: number;
  ariaLabel: string;
}

const W = 600;
const PAD = { top: 12, right: 64, bottom: 22, left: 34 };

const LineChart: React.FC<Props> = ({ series, xTicks, height = 200, ariaLabel }) => {
  const drawable = series.filter(s => s.points.length > 0);
  if (drawable.length === 0 || xTicks.length === 0) return null;

  const H = height;
  const maxY = Math.max(1, ...drawable.flatMap(s => s.points.map(p => p.y)));
  const yMax = Math.ceil(maxY * 1.1);
  const x0 = Math.min(...xTicks); const x1 = Math.max(...xTicks);
  const px = (x: number) =>
    x1 === x0 ? PAD.left + (W - PAD.left - PAD.right) / 2
              : PAD.left + ((x - x0) / (x1 - x0)) * (W - PAD.left - PAD.right);
  const py = (y: number) => H - PAD.bottom - (y / yMax) * (H - PAD.top - PAD.bottom);

  const gridYs = [0.25, 0.5, 0.75, 1].map(f => yMax * f);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={ariaLabel}>
      {gridYs.map(g => (
        <g key={g}>
          <line x1={PAD.left} x2={W - PAD.right} y1={py(g)} y2={py(g)} stroke="#CFCDC6" strokeWidth="0.5" />
          <text x={PAD.left - 6} y={py(g) + 3} fontSize="10" fill="#6E6960" textAnchor="end">{Math.round(g)}</text>
        </g>
      ))}
      {xTicks.map(t => (
        <text key={t} x={px(t)} y={H - 6} fontSize="10" fill="#6E6960" textAnchor="middle">W{t}</text>
      ))}
      {drawable.map(s => {
        const pts = [...s.points].sort((a, b) => a.x - b.x);
        const last = pts[pts.length - 1];
        const short = s.label.length > 9 ? s.label.slice(0, 8) + '…' : s.label;
        return (
          <g key={s.label}>
            {pts.length > 1 && (
              <polyline
                points={pts.map(p => `${px(p.x)},${py(p.y)}`).join(' ')}
                fill="none"
                stroke={s.color}
                strokeWidth={s.emphasis ? 2.5 : 1.5}
                strokeDasharray={s.dashed ? '4 3' : undefined}
              />
            )}
            {pts.length === 1 && !s.emphasis && (
              <circle cx={px(pts[0].x)} cy={py(pts[0].y)} r="2.5" fill={s.color} />
            )}
            {s.emphasis && (
              <>
                <circle cx={px(last.x)} cy={py(last.y)} r="3.5" fill={s.color} />
                <text x={px(last.x) + 7} y={py(last.y) + 3} fontSize="10" fontWeight="600" fill={s.color}>
                  {short}
                </text>
              </>
            )}
            <title>{`${s.label}: ${pts.map(p => `W${p.x} ${p.y}`).join(', ')}`}</title>
          </g>
        );
      })}
    </svg>
  );
};

export default LineChart;
